import React, { useCallback } from 'react'
import { usePagination } from '@material-ui/lab/Pagination'
import { Icon } from '@mondra/ui-components'


interface IPaginationProps {
    count?: number
    onChange: any
    page: number
    pageSize?: number
    totalRecords?: number
}

const defaultClass
    = 'inline-flex items-center text-sm font-medium border-t-2 py-4 px-4 focus:outline-none'

const getPaginationItems = (items: any[]) => {
    const list: any[] = []
    let next: React.ReactNode, previous: React.ReactNode

    items.forEach(({ page, type, selected, ...item }: any) => {
        if (type === 'previous') {
            previous = (
                <button
                    key="prev"
                    className={`${defaultClass} text-gray-500 border-transparent pl-1 hover:border-gray-300 disabled:opacity-50`}
                    {...item}
                >
                    <Icon type="arrowLeft" size={16} className="mr-2 text-gray-400" />
                    Previous
                </button>
            )
        } else if (type === 'next') {
            next = (
                <button
                    key="next"
                    className={`${defaultClass} text-gray-500 border-transparent pr-1 hover:border-gray-300 disabled:opacity-50`}
                    {...item}
                >
                    Next
                    <Icon type="arrowRight" size={16} className="ml-2 text-gray-400" />
                </button>
            )
        } else if (type === 'start-ellipsis' || type === 'end-ellipsis') {
            list.push(
                <span key="more" className={`${defaultClass} text-gray-500 border-transparent`}>
                    ...
                </span>
            )
        } else if (type === 'page') {
            list.push(
                <button
                    key={page}
                    className={`${defaultClass} ${
                        selected
                            ? 'border-primary-500 text-primary-600 cursor-not-allowed'
                            : 'border-transparent text-gray-500 hover:text-primary-600 hover:border-gray-300'
                    }`}
                    {...{ ...item, disabled: selected }}
                >
                    {page}
                </button>
            )
        }
    })

    return { list, next, previous }
}

export const Pagination: React.FunctionComponent<IPaginationProps> = ({
    onChange,
    page = 1,
    pageSize = 1,
    totalRecords = 1,
}) => {
    const count = Math.ceil(totalRecords / pageSize)

    const onPageChange = useCallback((event: any, page: number) => {
        onChange(page)
    }, [])

    const { items } = usePagination({
        count,
        onChange: onPageChange,
        page,
    })

    const { list, next, previous } = getPaginationItems(items)

    return (
        <div className="px-4 -mt-px border-t border-gray-200">
            <nav className="-mt-px flex items-center justify-between sm:px-0">
                <div className="w-0 flex-1 flex">{previous}</div>
                <div className="hidden md:flex">{list}</div>
                <div className="w-0 flex-1 flex justify-end">{next}</div>
            </nav>
        </div>
    )
}

export default Pagination
